import {
  REGISTER_RECENCY_STRATA,
  sanitizeRegisterRecencyStratum,
} from "../constants/registerRecencyStrata";
import { compareEntriesByGradation, getLastGameEntry, getWordGameCount } from "./registerWordListDisplay";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function getEntryStats(entry) {
  if (entry?.stats && typeof entry.stats === "object") {
    return entry.stats;
  }
  return entry;
}

function parseLastPlayCalendarDate(entry) {
  const last = getLastGameEntry(getEntryStats(entry));
  if (!last?.date) return null;

  const match = String(last.date).trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  if (!Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(year)) {
    return null;
  }

  return new Date(year, month - 1, day);
}

function parseLastPlayTimeMinutes(entry) {
  const last = getLastGameEntry(getEntryStats(entry));
  if (!last?.time || typeof last.time !== "string") return 0;

  const parts = last.time.trim().split(":");
  if (parts.length < 2) return 0;

  const hh = Number(parts[0]);
  const mm = Number(parts[1]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return 0;
  return hh * 60 + mm;
}

/** Jours calendaires depuis la dernière apparition en jeu ; null si jamais joué. */
export function getDaysSinceLastPlay(entry, now = new Date()) {
  if (getWordGameCount(entry) === 0) return null;

  const lastDate = parseLastPlayCalendarDate(entry);
  if (!lastDate) return null;

  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startLast = new Date(
    lastDate.getFullYear(),
    lastDate.getMonth(),
    lastDate.getDate()
  );
  const days = Math.round((startToday - startLast) / MS_PER_DAY);
  if (!Number.isFinite(days) || days < 0) return null;
  return days;
}

function daysMatchStratum(days, stratum) {
  if (days == null || !stratum) return false;

  const { minDaysExclusive, maxDaysInclusive } = stratum;

  if (minDaysExclusive == null) {
    return maxDaysInclusive == null || days <= maxDaysInclusive;
  }
  if (maxDaysInclusive == null) {
    return days > minDaysExclusive;
  }
  return days > minDaysExclusive && days <= maxDaysInclusive;
}

export function entryMatchesRecencyStratum(entry, stratumId, now = new Date()) {
  const days = getDaysSinceLastPlay(entry, now);
  if (days == null) return false;

  const id = sanitizeRegisterRecencyStratum(stratumId);
  const stratum = REGISTER_RECENCY_STRATA.find((item) => item.id === id);
  return daysMatchStratum(days, stratum);
}

export function getRecencyStratumIdForDays(days) {
  if (days == null || !Number.isFinite(days) || days < 0) return null;
  return REGISTER_RECENCY_STRATA.find((stratum) => daysMatchStratum(days, stratum))?.id ?? null;
}

/** Filtre + tri (plus récent d’abord, ou par gradation) pour la strate active. */
export function filterEntriesByRecencyStratum(
  entries,
  stratumId,
  { now = new Date(), sortByGradation = false, masteredFirst = false } = {}
) {
  return (Array.isArray(entries) ? entries : [])
    .filter((entry) => entryMatchesRecencyStratum(entry, stratumId, now))
    .sort((a, b) => {
      if (sortByGradation) {
        return compareEntriesByGradation(a, b, masteredFirst);
      }

      const daysA = getDaysSinceLastPlay(a, now);
      const daysB = getDaysSinceLastPlay(b, now);
      if (daysA !== daysB) return daysA - daysB;

      const timeA = parseLastPlayTimeMinutes(a);
      const timeB = parseLastPlayTimeMinutes(b);
      if (timeA !== timeB) return timeB - timeA;

      return String(a?.mot || "").localeCompare(String(b?.mot || ""), "fr", {
        sensitivity: "base",
      });
    });
}
